import { ServiceError, ServiceApiResponse } from "../types/unified-services";

export class UnifiedServiceError extends Error {
  public readonly type: ServiceError["type"];
  public readonly code?: string;
  public readonly details?: any;

  constructor(type: ServiceError["type"], message: string, code?: string, details?: any) {
    super(message);
    this.name = "UnifiedServiceError";
    this.type = type;
    this.code = code;
    this.details = details;
  }
}

export const createNetworkError = (message: string = "Network connection failed"): UnifiedServiceError => {
  return new UnifiedServiceError("NETWORK", message, "NETWORK_ERROR");
};

export const createValidationError = (message: string, details?: any): UnifiedServiceError => {
  return new UnifiedServiceError("VALIDATION", message, "VALIDATION_ERROR", details);
};

export const createApiError = (message: string, code?: string, details?: any): UnifiedServiceError => {
  return new UnifiedServiceError("API", message, code || "API_ERROR", details);
};

export const createProcessingError = (message: string, details?: any): UnifiedServiceError => {
  return new UnifiedServiceError("PROCESSING", message, "PROCESSING_ERROR", details);
};

export const createPermissionError = (message: string, details?: any): UnifiedServiceError => {
  return new UnifiedServiceError("PERMISSION", message, "PERMISSION_ERROR", details);
};

export const handleApiResponse = <T>(response: ServiceApiResponse<T>): T => {
  if (response.status_code !== 200) {
    throw createNetworkError(`HTTP ${response.status_code}: ${response.message}`);
  }

  if (!response.ok || response.status_code !== 200) {
    throw createApiError(response.message || "Service returned an error", `API_${response.status_code}`);
  }

  if (!response.data) {
    throw createApiError("No data received from service", "NO_DATA");
  }

  return response.data;
};

export const formatErrorMessage = (error: unknown): string => {
  if (error instanceof UnifiedServiceError) {
    return error.message;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "An unexpected error occurred";
};

export const isNetworkError = (error: unknown): boolean => {
  return error instanceof UnifiedServiceError && error.type === "NETWORK";
};

export const isValidationError = (error: unknown): boolean => {
  return error instanceof UnifiedServiceError && error.type === "VALIDATION";
};

export const isApiError = (error: unknown): boolean => {
  return error instanceof UnifiedServiceError && error.type === "API";
};

export const isProcessingError = (error: unknown): boolean => {
  return error instanceof UnifiedServiceError && error.type === "PROCESSING";
};

export const isPermissionError = (error: unknown): boolean => {
  return error instanceof UnifiedServiceError && error.type === "PERMISSION";
};

export const shouldRetry = (error: unknown): boolean => {
  if (error instanceof UnifiedServiceError) {
    return error.type === "NETWORK";
  }
  return false;
};

export const logError = (error: unknown, context?: string): void => {
  const errorMessage = formatErrorMessage(error);
  const logContext = context ? `[${context}]` : "";

  console.error(`${logContext} Service Error:`, {
    message: errorMessage,
    error:
      error instanceof UnifiedServiceError
        ? {
            type: error.type,
            code: error.code,
            details: error.details,
          }
        : error,
    timestamp: new Date().toISOString(),
  });
};
